import { useEffect } from 'react';
import { overlayModule, useOverlayModule } from '../lazyOverlay';
import type { PeriodPopoverProps } from './PeriodPopover';

// The lazy boundary in front of the date-range panel (T-321 / BUG-155; REQ-280 AC1, ARCH §8.1).
//
// The top bar's date BUTTON is the first chunk's business — it carries the range every number on the page is
// about, and `shell.period` / `shell.period.dates` are steady ids of every framed route. The PANEL behind it
// is not: the seven preset rows, the two-month calendar grid, the custom-range draft rules and the compare
// line (`PeriodPopover` + `Calendar` + `customRange`, 8.7 KB of the eager chunk's generated bytes) exist only
// while the popover is open, which is why they are declared as an overlay rather than as steady ids
// (`PERIOD_CONTROL_TEST_IDS`, with `shell.period.trigger` as the trigger that reaches them). A reader who
// never opens it downloaded all of it before the first number.
//
// The panel opens on a press, so the chunk is PREFETCHED ON IDLE (the `search/lazy.tsx` pattern): by the time
// anyone reaches for the button the module is in memory and the panel is drawn in the same commit as the
// press. The idle callback is what keeps it off the critical path — the same lesson as the neighbouring-period
// prefetch in `PeriodProvider.tsx`, which is what BUG-155 was about.
//
// T-323 / BUG-157: the boundary is `app/lazyOverlay.ts`, not `React.lazy` + `Suspense` — a boundary that
// suspends on the press that mounts it livelocks React's synchronous `act()` flush and eats a 4 GB heap. The
// comment there has the whole story. What is on screen is unchanged: nothing until the module is in hand
// (which `fallback={null}` also meant), then the panel, over a page that is already there; the trigger keeps
// `aria-expanded` either way, so the button never lies about its state.

const panel = overlayModule(() => import('./PeriodPopover'));

/** Warm the chunk. Safe to call repeatedly: the load is shared. */
export function prefetchPeriodPopover(): void {
  void panel.load();
}

/** Ask for the chunk once the browser is idle; the handle is cancelled on unmount. */
export function usePeriodPopoverPrefetch(): void {
  useEffect(() => {
    const idle = (globalThis as { requestIdleCallback?: typeof requestIdleCallback })
      .requestIdleCallback;
    if (typeof idle === 'function') {
      const handle = idle(() => prefetchPeriodPopover(), { timeout: 3000 });
      const cancel = (globalThis as { cancelIdleCallback?: typeof cancelIdleCallback })
        .cancelIdleCallback;
      return () => cancel?.(handle);
    }
    const timer = setTimeout(prefetchPeriodPopover, 1500);
    return () => clearTimeout(timer);
  }, []);
}

/** The panel, code-split. Nothing is rendered, mounted or downloaded until it is opened. */
export function LazyPeriodPopover(props: PeriodPopoverProps) {
  const module = useOverlayModule(panel, props.open);
  if (!props.open || module === null) return null;
  const PeriodPopover = module.PeriodPopover;
  return <PeriodPopover {...props} />;
}
